'use client';

import { c as _c } from "react/compiler-runtime";
import { Card } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger, Text } from '@repo/ui';
import { Chevron } from '@repo/ui/icons';
import { Fragment, type ReactNode } from 'react';
import { type FeedBranchGroup, type FeedSideGroup, isEmptySlot, isFixedOne } from './groupBySide';
import { AdminValue, ExplorerLink, FeedName, hasListedAs, LastUpdateValue, Muted, ProviderLabel, SideLabel, StaleMark, TypeBadge, useIsTruncated } from './OracleFeedParts';
import { type FeedRow, formatDeviation, formatFeedValue, formatHeartbeat, hasFeedDetails, typeBadgeLabel } from './oracleFeeds';
function GroupHeader(t0) {
  const $ = _c(2);
  const {
    group
  } = t0;
  let t1;
  if ($[0] !== group) {
    t1 = <div className="px-md py-xs text-3xs text-secondary-foreground">{group ? <SideLabel group={group} /> : <span className="text-muted-foreground">Oracle</span>}</div>;
    $[0] = group;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function DetailLine(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-muted-foreground text-xs">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span className="text-foreground min-w-0 text-right text-xs break-words">{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="gap-xs flex min-h-[30px] items-center justify-between">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
const ROW = 'gap-sm py-sm relative flex w-full items-center';
function FeedCardRow(t0) {
  const $ = _c(66);
  const {
    chainId,
    row,
    openIds,
    onToggle
  } = t0;
  const open = openIds.has(row.id);
  const [nameRef, truncated] = useIsTruncated();
  let t1;
  if ($[0] !== chainId || $[1] !== nameRef || $[2] !== row) {
    t1 = isFixedOne(row) ? <Text tag="span" size="2xs" className="text-foreground relative z-10 flex h-lh min-w-0 items-center"><FeedName chainId={chainId} row={row} /></Text> : <Text ref={nameRef} tag="span" size="2xs" className="text-foreground min-w-0 truncate">{row.name}</Text>;
    $[0] = chainId;
    $[1] = nameRef;
    $[2] = row;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== row) {
    t2 = typeBadgeLabel(row) !== null && <span className="relative z-10 inline-flex shrink-0"><TypeBadge type={row.type} providerType={row.providerType} /></span>;
    $[4] = row;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <span className="gap-xs flex min-w-0 flex-1 items-center">{t1}{t2}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = row.price === null ? "text-secondary-foreground" : "text-foreground";
  let t5;
  if ($[9] !== t4) {
    t5 = cn("gap-2xs inline-flex items-center whitespace-nowrap tabular-nums", t4);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== row.price) {
    t6 = formatFeedValue(row.price);
    $[11] = row.price;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== row) {
    t7 = <StaleMark row={row} />;
    $[13] = row;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <Text tag="span" size="2xs" className={t5}>{t6}{t7}</Text>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t3 || $[20] !== t8) {
    t9 = <>{t3}{t8}</>;
    $[19] = t3;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const summary = t9;
  let t10;
  if ($[22] !== chainId || $[23] !== onToggle || $[24] !== openIds || $[25] !== row.children) {
    let t11;
    if ($[27] !== chainId || $[28] !== onToggle || $[29] !== openIds) {
      t11 = child => <FeedCardRow key={child.id} chainId={chainId} row={child} openIds={openIds} onToggle={onToggle} />;
      $[27] = chainId;
      $[28] = onToggle;
      $[29] = openIds;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    t10 = row.children.map(t11);
    $[22] = chainId;
    $[23] = onToggle;
    $[24] = openIds;
    $[25] = row.children;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const children = t10;
  if (!hasFeedDetails(row) || isEmptySlot(row)) {
    let t11;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = cn(ROW, "px-md");
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <span aria-hidden={true} className="size-4 shrink-0" />;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    let t13;
    if ($[33] !== summary) {
      t13 = <div className={t11}>{summary}{t12}</div>;
      $[33] = summary;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] !== children || $[36] !== t13) {
      t14 = <>{t13}{children}</>;
      $[35] = children;
      $[36] = t13;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    return t14;
  }
  const T0 = Collapsible;
  let t11;
  if ($[38] !== onToggle || $[39] !== row.id) {
    t11 = next => onToggle(row.id, next);
    $[38] = onToggle;
    $[39] = row.id;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const t12 = "data-[state=open]:bg-muted flex flex-col";
  let t13;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn(ROW, "hover:bg-accent has-[>button:active]:bg-accent transition-colors", "px-md");
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== row.name) {
    t14 = <CollapsibleTrigger aria-label={row.name} className="focus-visible:outline-ring absolute inset-0 cursor-pointer outline-none focus-visible:outline-2 focus-visible:-outline-offset-2" />;
    $[42] = row.name;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const T1 = Chevron;
  const t15 = true;
  const t16 = cn("text-secondary-foreground size-4 shrink-0 transition-transform", open && "rotate-180");
  let t17;
  if ($[44] !== T1 || $[45] !== t16) {
    t17 = <T1 aria-hidden={t15} className={t16} />;
    $[44] = T1;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== summary || $[48] !== t13 || $[49] !== t14 || $[50] !== t17) {
    t18 = <div className={t13}>{t14}{summary}{t17}</div>;
    $[47] = summary;
    $[48] = t13;
    $[49] = t14;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = cn("relative overflow-hidden", "px-md");
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== chainId || $[54] !== row || $[55] !== truncated) {
    t20 = <CollapsibleContent className={t19}><div className="pb-xs flex flex-col"><FeedDetails chainId={chainId} row={row} truncated={truncated} /></div></CollapsibleContent>;
    $[53] = chainId;
    $[54] = row;
    $[55] = truncated;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== T0 || $[58] !== open || $[59] !== t11 || $[60] !== t18 || $[61] !== t20) {
    t21 = <T0 open={open} onOpenChange={t11} className={t12}>{t18}{t20}</T0>;
    $[57] = T0;
    $[58] = open;
    $[59] = t11;
    $[60] = t18;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== children || $[64] !== t21) {
    t22 = <>{t21}{children}</>;
    $[63] = children;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  return t22;
}
function FeedDetails(t0) {
  const $ = _c(36);
  const {
    chainId,
    row,
    truncated
  } = t0;
  let t1;
  if ($[0] !== row) {
    t1 = isEmptySlot(row);
    $[0] = row;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const emptySlot = t1;
  let t2;
  if ($[2] !== row.name || $[3] !== truncated) {
    t2 = truncated && <DetailLine label="Feed">{row.name}</DetailLine>;
    $[2] = row.name;
    $[3] = truncated;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== row) {
    t3 = hasListedAs(row) && <DetailLine label="Listed as">{row.listedAs}</DetailLine>;
    $[5] = row;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== chainId || $[8] !== row.address) {
    t4 = row.address && <DetailLine label="Contract"><ExplorerLink chainId={chainId} address={row.address} /></DetailLine>;
    $[7] = chainId;
    $[8] = row.address;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== emptySlot || $[11] !== row) {
    t5 = <DetailLine label="Provider">{emptySlot ? <Muted /> : <ProviderLabel feed={row} />}</DetailLine>;
    $[10] = emptySlot;
    $[11] = row;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== chainId || $[14] !== emptySlot || $[15] !== row.admins) {
    t6 = <DetailLine label="Admin">{emptySlot ? <Muted /> : <AdminValue chainId={chainId} admins={row.admins} />}</DetailLine>;
    $[13] = chainId;
    $[14] = emptySlot;
    $[15] = row.admins;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== row) {
    t7 = formatHeartbeat(row) ?? <Muted />;
    $[17] = row;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <DetailLine label="Heartbeat">{t7}</DetailLine>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== row.deviation) {
    t9 = row.deviation === null ? <Muted /> : formatDeviation(row.deviation);
    $[21] = row.deviation;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t9) {
    t10 = <DetailLine label="Deviation threshold">{t9}</DetailLine>;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== row) {
    t11 = <DetailLine label="Last price update"><LastUpdateValue row={row} /></DetailLine>;
    $[25] = row;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t2 || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t8) {
    t12 = <>{t2}{t3}{t4}{t5}{t6}{t8}{t10}{t11}</>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t8;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}

/** Mobile: one table-like card per oracle branch, a header per side, and a row per feed that opens in place. */
export function OracleFeedsCards(t0) {
  const $ = _c(6);
  const {
    chainId,
    branch,
    openIds,
    onToggle
  } = t0;
  let t1;
  if ($[0] !== branch.oracleRows || $[1] !== branch.sides || $[2] !== chainId || $[3] !== onToggle || $[4] !== openIds) {
    t1 = <Card padding="none" withBorder={true} className="flex-col overflow-hidden"><div className="divide-border flex flex-col divide-y-[0.5px]">{[...(branch.oracleRows.length ? [{
          key: "oracle",
          group: null,
          rows: branch.oracleRows
        }] : []), ...branch.sides.map(_temp)].map(t2 => {
          const {
            key,
            group: group_0,
            rows
          } = t2;
          return <Fragment key={key}><GroupHeader group={group_0} />{rows.map(row => <FeedCardRow key={row.id} chainId={chainId} row={row} openIds={openIds} onToggle={onToggle} />)}</Fragment>;
        })}</div></Card>;
    $[0] = branch.oracleRows;
    $[1] = branch.sides;
    $[2] = chainId;
    $[3] = onToggle;
    $[4] = openIds;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function _temp(group) {
  return {
    key: group.side,
    group,
    rows: group.rows
  };
}